// src/components/Leave/LeaveDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiCalendar, FiUser, FiBriefcase, FiFileText,
  FiClock, FiCheckCircle, FiXCircle, FiEdit2, FiTrash2
} from 'react-icons/fi';
import { useAppContext } from '../../context/AppContext';
import StatusBadge from '../Common/StatusBadge';

const LeaveDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { leave } = useAppContext();
  const { getMyRequests, cancelLeave, withdrawLeave, deleteLeave, loading } = leave;

  const [leaveData, setLeaveData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const loadLeave = async () => {
    setError(null);
    setIsLoading(true);
    try {
      const data = await getMyRequests({ id });

      // Handle different response formats
      if (data && typeof data === 'object') {
        if (data.results && Array.isArray(data.results)) {
          // If paginated response
          const found = data.results.find(item => item.id === parseInt(id));
          setLeaveData(found || null);
        } else if (data.id) {
          // If direct object
          setLeaveData(data);
        } else if (Array.isArray(data)) {
          // If array
          const found = data.find(item => item.id === parseInt(id));
          setLeaveData(found || null);
        } else {
          setLeaveData(null);
        }
      } else {
        setLeaveData(null);
      }
    } catch (err) {
      setError(err.message || 'Failed to load leave details');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadLeave();
    }
  }, [id]);

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this leave request?')) return;

    setActionLoading(true);
    try {
      await cancelLeave(id);
      navigate('/leave/history');
    } catch (err) {
      setError(err.message || 'Failed to cancel leave');
    } finally {
      setActionLoading(false);
    }
  };

  const handleWithdraw = async () => {
    if (!window.confirm('Are you sure you want to withdraw this leave request?')) return;

    setActionLoading(true);
    try {
      await withdrawLeave(id);
      navigate('/leave/history');
    } catch (err) {
      setError(err.message || 'Failed to withdraw leave');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this draft?')) return;

    setActionLoading(true);
    try {
      await deleteLeave(id);
      navigate('/leave/history');
    } catch (err) {
      setError(err.message || 'Failed to delete leave');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/leave/edit/${id}`);
  };

  const handleSubmit = () => {
    navigate(`/leave/submit/${id}`);
  };

  // Show loading
  if ((loading || isLoading) && !leaveData) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Show error
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {error}
        <button onClick={loadLeave} className="ml-3 text-sm font-medium hover:underline">
          Retry
        </button>
      </div>
    );
  }

  // Show not found
  if (!leaveData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Leave request not found</p>
        <button
          onClick={() => navigate('/leave/history')}
          className="mt-3 text-indigo-600 hover:text-indigo-700 font-medium"
        >
          Back to History
        </button>
      </div>
    );
  }

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/leave/history')}
          className="p-2 hover:bg-gray-100 rounded-lg transition"
        >
          <FiArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-800">Leave Request #{leaveData.id}</h1>
            <StatusBadge status={leaveData.status} />
          </div>
          <p className="text-gray-500">
            Applied on {formatDateTime(leaveData.applied_at)}
          </p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Leave Details */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Leave Details</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Leave Type</p>
                  <p className="font-medium text-gray-800">
                    {leaveData.leave_type_display || leaveData.leave_type}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Days</p>
                  <p className="font-medium text-gray-800">
                    {leaveData.total_days} day(s)
                  </p>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-500">Date Range</p>
                <p className="font-medium text-gray-800">
                  {formatDate(leaveData.start_date)} → {formatDate(leaveData.end_date)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Reason</p>
                <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">
                  {leaveData.reason || 'No reason provided'}
                </p>
              </div>
              {leaveData.attachment_url && (
                <div>
                  <p className="text-sm text-gray-500">Attachment</p>
                  <a
                    href={leaveData.attachment_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    View Attachment →
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Approval/Rejection Details */}
          {(leaveData.status === 'approved' || leaveData.status === 'rejected') && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                {leaveData.status === 'approved' ? 'Approval Details' : 'Rejection Details'}
              </h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  {leaveData.status === 'approved' ? (
                    <FiCheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <FiXCircle className="w-5 h-5 text-red-500" />
                  )}
                  <div>
                    <p className="font-medium text-gray-800">
                      {leaveData.status === 'approved' ? 'Approved' : 'Rejected'}
                      {leaveData.approved_by_name ? ` by ${leaveData.approved_by_name}` : ''}
                    </p>
                    {leaveData.approved_at && (
                      <p className="text-sm text-gray-500">
                        {formatDateTime(leaveData.approved_at)}
                      </p>
                    )}
                  </div>
                </div>
                {leaveData.rejection_reason && (
                  <div className="bg-red-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-500">Rejection Reason</p>
                    <p className="text-gray-700">{leaveData.rejection_reason}</p>
                  </div>
                )}
                {leaveData.approval_remarks && (
                  <div className="bg-green-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-500">Remarks</p>
                    <p className="text-gray-700">{leaveData.approval_remarks}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar - Actions */}
        <div className="space-y-6">
          {/* Actions Card */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Actions</h3>
            <div className="space-y-3">

              {leaveData.can_cancel && leaveData.status === 'pending' && (
                <button
                  onClick={handleCancel}
                  disabled={actionLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-yellow-50 text-yellow-600 rounded-lg hover:bg-yellow-100 transition disabled:opacity-50"
                >
                  {actionLoading ? 'Processing...' : 'Cancel Request'}
                </button>
              )}
              {leaveData.can_withdraw && (
                <button
                  onClick={handleWithdraw}
                  disabled={actionLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100 transition disabled:opacity-50"
                >
                  {actionLoading ? 'Processing...' : 'Withdraw Request'}
                </button>
              )}
              {leaveData.status === 'draft' && (
                <>
                  <button
                    onClick={handleDelete}
                    disabled={actionLoading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition disabled:opacity-50"
                  >
                    <FiTrash2 className="w-4 h-4" />
                    {actionLoading ? 'Deleting...' : 'Delete Draft'}
                  </button>
                  <button
                    onClick={handleSubmit}
                    className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                  >
                    Submit for Approval
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Info Card */}
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Request Information</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Request ID</span>
                <span className="font-medium text-gray-800">#{leaveData.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Applied On</span>
                <span className="font-medium text-gray-800">
                  {formatDateTime(leaveData.applied_at)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Last Updated</span>
                <span className="font-medium text-gray-800">
                  {formatDateTime(leaveData.updated_at)}
                </span>
              </div>
              {leaveData.expires_at && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Expires On</span>
                  <span className="font-medium text-gray-800">
                    {formatDate(leaveData.expires_at)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaveDetail;